import * as React from 'react';

import {
  IConfigObject,
  intentGraphDataSourceName,
  IReactiveConfigurationDataSource,
  IRuntimeHandlerConfig
} from '../types/configuration.types';

import { getDataSource } from './dataSources';

// tslint:disable no-any
function getDisplayName(Component: React.ComponentClass | React.FunctionComponent): string {
  return Component.displayName || Component.name || 'Component';
}

type ConfigMapConstrain<T> = { [K in keyof T]: T[K] extends IConfigObject ? T[K] : any };
type ConfigMapDataSource<T extends ConfigMapConstrain<T>> = { [K in keyof T]: IReactiveConfigurationDataSource<any> };
type ConfigMapValue<T extends ConfigMapConstrain<T>> = { [K in keyof T]: Partial<T[K]> };

/**
 * The props interface to have when wrapped by `withConfiguration` HOC
 */
export interface IComponentWithConfiguration<T extends ConfigMapConstrain<T>> {
  /**
   * Injected config data
   *
   * note that the Type T here should be defined strictly. i.e. if a field could be undefined
   * for a period of time ruing runtime, the type must say so.
   */
  config: ConfigMapValue<T>;
}

/**
 * The `withConfiguration` HOC consume the ReactiveConfigurationDataSource
 * and provides configuration data to the component wrapped
 */
export function withConfiguration<
  TConfigMap extends ConfigMapConstrain<TConfigMap>,
  TProps extends IComponentWithConfiguration<TConfigMap>,
  R = Pick<TProps, Exclude<keyof TProps, 'config'>>
>(
  Component: React.ComponentClass<TProps> | React.FC<TProps>,
  dataSources: ConfigMapDataSource<TConfigMap>
): React.ComponentType<R> {
  interface IWithConfigurationState {
    configData: ConfigMapValue<TConfigMap>;
  }
  return class WithConfiguration extends React.PureComponent<R, IWithConfigurationState> {
    public displayName = `withConfiguration(${getDisplayName(Component)})`;

    constructor(props: R) {
      super(props);

      // tslint:disable-next-line: no-any
      const newData: any = {};
      Object.keys(dataSources).forEach((key: string) => {
        // tslint:disable-next-line: no-any
        // const dataSource = (dataSources as any)[key];
        const newDataFromSource = getDataSource<IRuntimeHandlerConfig>(
          intentGraphDataSourceName
        ).register() as IRuntimeHandlerConfig; // dataSource.register(this.configUpdated);
        newData[key] = {
          ...(newDataFromSource as Object)
        };
      });
      // const configData = getDataSource<IRuntimeHandlerConfig>('yoda.ds.intentGraph').register() as IRuntimeHandlerConfig;
      this.state = { configData: newData };
    }

    public componentWillUnmount(): void {
      Object.keys(dataSources).forEach((key: string) => {
        // tslint:disable-next-line: no-any
        const dataSource = (dataSources as any)[key];
        dataSource.unregister(this.configUpdated);
      });
    }

    public render(): React.ReactNode {
      // tslint:disable-next-line: no-any
      const WrappedComponent = Component as any;
      return <WrappedComponent config={this.state.configData} {...this.props} />;
    }

    private configUpdated = (newData: Partial<TConfigMap>) => {
      // tslint:disable-next-line: ban
      this.setState({
        configData: {
          // tslint:disable-next-line: no-any
          ...(this.state.configData as any),
          ...(newData as Object)
        }
      });
    };
  };
}
